// components/teams/TeamHero.tsx
"use client";

import { Link } from "@/i18n/navigation";
import { Home } from 'lucide-react';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem,
  BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const DOMAIN_ICON: Record<string, string> = {
  education: '✦', math: '∫', code: '</>', science: '⚛', creative: '✎',
};

function HexDecor({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 115" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <polygon
        points="50,5 95,27.5 95,87.5 50,110 5,87.5 5,27.5"
        stroke="rgb(var(--accent))"
        strokeWidth="1.5"
        fill="none"
      />
    </svg>
  );
}

interface LayerConfig {
  label: string; bg: string; text: string;
  border: string; stripe: string; fill: string;
}

const ALL_LAYERS: Record<number, LayerConfig> = {
  1: { label: 'L1', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', stripe: 'bg-violet-400', fill: '#7c3aed' },
  2: { label: 'L2', bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200',   stripe: 'bg-teal-500',   fill: '#0d9488' },
  3: { label: 'L3', bg: 'bg-teal-50',   text: 'text-teal-800',   border: 'border-teal-300',   stripe: 'bg-teal-600',   fill: '#0f766e' },
  4: { label: 'L4', bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   stripe: 'bg-blue-500',   fill: '#2563eb' },
  5: { label: 'L5', bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  stripe: 'bg-amber-500',  fill: '#d97706' },
  6: { label: 'L6', bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    stripe: 'bg-red-500',    fill: '#dc2626' },
};

interface Props {
  name: string;
  desc: string;
  domain: string;
  layer: number;
  slug: string;
  isEnabled: boolean;
  isB2bOnly: boolean;
  lc: LayerConfig;
  layerLabel: string;
  ui: Record<string, string>;
}

export default function TeamHero({
  name, desc, domain, layer, slug,
  isEnabled, isB2bOnly, lc, layerLabel, ui,
}: Props) {

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: name, text: desc, url });
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  // Шестигранники в сотах — координаты центров
    const hexCenters = [
    { l: 1, cx: 90,  cy: 100 },
    { l: 2, cx: 90,  cy: 56  },
    { l: 3, cx: 128, cy: 78  },
    { l: 4, cx: 128, cy: 122 },
    { l: 5, cx: 90,  cy: 144 },
    { l: 6, cx: 52,  cy: 122 },
    { l: 7, cx: 52,  cy: 78  },
  ];

  return (
    <section className="relative overflow-hidden bg-white">
            

      {/* Точечная сетка */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle, rgb(var(--accent) / 0.35) 1px, transparent 1px)`,
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 60% 90% at 15% 50%, black, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 90% at 15% 50%, black, transparent 80%)',
        }}
      />

      {/* Радиальное свечение */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 45% 60% at 15% 50%, rgb(var(--accent) / 0.06) 0%, transparent 70%)',
        }}
      />

      {/* Декоративные гексагоны слева */}
      <HexDecor className="absolute left-[2%] top-10 w-14 h-14 opacity-20" />
      <HexDecor className="absolute left-[7%] bottom-10 w-20 h-20 opacity-10" />
      <HexDecor className="absolute left-[4%] top-1/2 -translate-y-1/2 w-8 h-8 opacity-15" />

      {/* Контент */}
     {/*<div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-12 flex flex-col lg:flex-row gap-12 items-start lg:items-center justify-between"> */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-12">
        {/* Левая колонка */}
        <div className="flex flex-row items-center gap-8 flex-wrap">
          <div className="flex-1 min-w-[280px]">

          {/* Домен */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-base">{DOMAIN_ICON[domain] ?? '✦'}</span>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest capitalize">
              {domain}
            </span>
          </div>

          {/* H1 */}
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-slate-900 mb-4 leading-tight">
            {name}
          </h1>

          {/* Описание */}
          <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-lg">
            {desc}
          </p>

          {/* Кнопки */}
          <div className="flex items-center gap-3 flex-wrap">
            {isB2bOnly ? (
              <a
                href="mailto:hello@layvers.com"
                className="px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-700 transition-colors"
              >
                {ui.contactUs}
              </a>
            ) : isEnabled ? (
              <Link
                href={`/chat?team=${slug}` as any}
                className="px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                {ui.tryNow}
              </Link>
            ) : (
              <span className="px-6 py-3 rounded-xl bg-slate-100 text-slate-400 font-semibold cursor-not-allowed">
                {ui.comingSoon}
              </span>
            )}

            <button
              onClick={handleShare}
              className="px-5 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {ui.share}
            </button>

            
          </div>
        </div>

        {/* Правая колонка — шестигранники в сотах */}
       <div className="flex-shrink-0 flex flex-col items-center gap-1">
          <svg width="200" height="230" viewBox="0 0 180 220" fill="none">
            {hexCenters.map(({ l, cx, cy }) => {
              const isDecor = l === 7;
              const isFilled = !isDecor && l <= layer;
              const isBig = l === 1;
              const c = ALL_LAYERS[Math.min(l, 6)];
              const activeColor = ALL_LAYERS[layer]?.fill ?? '#7c3aed';
              const size = isBig ? 32 : 22;

              const pts = Array.from({ length: 6 }, (_, i) => {
                const angle = (Math.PI / 180) * (60 * i - 30);
                return `${cx + size * Math.cos(angle)},${cy + size * Math.sin(angle)}`;
              }).join(' ');
              return (
                <g key={l}>
                  <polygon
                    points={pts}
                    fill={isDecor ? 'transparent' : isFilled ? activeColor : '#f8fafc'}
                    stroke={isDecor ? '#e2e8f0' : isFilled ? activeColor : '#e2e8f0'}
                    strokeWidth={isFilled ? 2 : 1}
                    opacity={isDecor ? 0.4 : 1}
                  />
                  <text
                    x={cx} y={cy + 4}
                    textAnchor="middle"
                    fontSize={isBig ? 11 : 9}
                    fontWeight={isFilled ? 700 : 400}
                    fill={isDecor ? '#cbd5e1' : isFilled ? 'white' : '#94a3b8'}
                  >
                    L{l}
                  </text>
                </g>
              );
              
            })}
            {/* Подпись текущего уровня */}
            <rect x="45" y="178" width="90" height="22" rx="11"
            fill={lc.fill} opacity="0.15" />
              <text x="90" y="193" textAnchor="middle"
             fontSize="10" fontWeight="700" fill={lc.fill}>
             {layerLabel}
           </text>
          </svg>
          

          
         
        </div>
       </div>
      </div>
    </section>
  );
}